import { useReducer } from 'react';
import { IMessage } from '@novu/headless';

export interface State {
	notifications: IMessage[];
	hasMore: boolean;
}

const defaultState: State = {
	notifications: [],
	hasMore: false,
};

export interface UpdatePayload {
	incomingNotifications: IMessage[];
	incomingHasMore: boolean;
	currentPageNum: number;
}

export enum ActionTypes {
	AddNewNotifications = 'AddNewMessages',
	MarkAllAsRead = 'MarkAllAsRead',
	MarkAsRead = 'MarkAsRead',
	DeleteAllNotifications = 'DeleteAllNotifications',
}

interface Action<T = any> {
	type: ActionTypes;
	data?: T;
}

function isAddNewMessages(action: Action): action is Required<Action<UpdatePayload>> {
	return action.type === ActionTypes.AddNewNotifications;
}

function isMarkAllAsRead(action: Action): action is Required<Action> {
	return action.type === ActionTypes.MarkAllAsRead;
}

function isMarkAsRead(
	action: Action
): action is Required<Action<{ messageIds: string[] }>> {
	return action.type === ActionTypes.MarkAsRead;
}

function isDeleteAllNotifications(action: Action): action is Required<Action> {
	return action.type === ActionTypes.DeleteAllNotifications;
}

export function useNotificationStore() {
	return useReducer((current: State, payload: Action) => {
		if (isAddNewMessages(payload)) {
			const { incomingNotifications, incomingHasMore, currentPageNum } = payload.data;
			const notificationList = current.notifications;
			currentPageNum === 0
				? notificationList.unshift(...incomingNotifications)
				: notificationList.push(...incomingNotifications);

			const notificationListDeduped = notificationList.filter(
				(notification, i, list) =>
					list.findIndex(notification_2 => notification_2._id === notification._id) === i
			);
			const hasMoreUpdate =
				currentPageNum === 0
					? (notificationListDeduped.length === 10 && incomingHasMore) || current.hasMore
					: incomingHasMore;

			return {
				...current,
				notifications: notificationListDeduped,
				hasMore: hasMoreUpdate,
			};
		}
		if (isDeleteAllNotifications(payload)) {
			return {
				...current,
				notifications: [],
				hasMore: false,
			};
		}
		if (isMarkAllAsRead(payload)) {
			const notificationList = current.notifications.map(notification => {
				notification.read = true;
				return notification;
			});
			return {
				...current,
				notifications: notificationList,
			};
		}
		if (isMarkAsRead(payload)) {
			const notificationList = current.notifications.map(notification => {
				if (payload.data.messageIds.includes(notification._id)) {
					notification.read = true;
				}
				return notification;
			});
			return {
				...current,
				notifications: notificationList,
			};
		}
		return current;
	}, defaultState);
}
